import { LoadingButton } from "@mui/lab";
import {
  Autocomplete,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import {
  endOfDay,
  endOfMonth,
  isValid,
  startOfDay,
  startOfMonth,
} from "date-fns";
import { useState } from "react";
import { Driver, Period, ReportType } from "../../../graphql/generated";
import driverLabel from "../../../utils/labels/driverLabel";
import reportTypeLabel from "../../../utils/labels/reportTypeLabel";
import { useTranslation } from "react-i18next";
import StateSelect from "../../common/StateSelect";
import { testIds } from "./ReportGenerator.testIds";

export type ReportFilter = {
  driver?: string | null | undefined;
  originState?: string | null | undefined;
  destinationState?: string | null | undefined;
};

type ReportGeneratorProps = {
  onGenerateAndDownloadReport: (
    reportType: ReportType,
    period: Period,
    filter: ReportFilter
  ) => void;
  onGenerateAndDownloadCsvReport: (
    reportType: ReportType,
    period: Period,
    filter: ReportFilter
  ) => void;
  onSendReport: (
    reportType: ReportType,
    period: Period,
    filter: ReportFilter,
    email: string
  ) => void;
  generating: boolean;
  generatingCsv: boolean;
  sending: boolean;
  drivers: Pick<Driver, "_id" | "firstname" | "lastname">[];
};

const ReportGenerator = ({
  generating,
  generatingCsv,
  onGenerateAndDownloadReport,
  onGenerateAndDownloadCsvReport,
  drivers,
  onSendReport,
  sending,
}: ReportGeneratorProps) => {
  const { t } = useTranslation("finance");
  const [reportType, setReportType] = useState<ReportType | null>(
    ReportType.RevenuePerDay
  );
  const [periodStart, setPeriodStart] = useState(startOfMonth(new Date()));
  const [periodEnd, setPeriodEnd] = useState(endOfMonth(new Date()));
  const [filter, setFilter] = useState<ReportFilter>({});
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [email, setEmail] = useState("");

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {t(
          "reportSelectDescription",
          "Select a report you want to generate and download"
        )}
      </Typography>
      <Stack direction="row" spacing={2}>
        <Stack direction="column" spacing={2}>
          <Select data-testid={testIds.reportGeneratorReportType}
            label="Report type"
            size="small"
            value={reportType}
            onChange={(event) => {
              setReportType(event.target.value as ReportType);
              setFilter({});
              if (event.target.value === ReportType.TripSheet) {
                setPeriodStart(startOfDay(new Date()));
                setPeriodEnd(endOfDay(new Date()));
              }
            }}
          >
            {Object.values(ReportType).map((target) => (
              <MenuItem data-testid={`${testIds.reportGeneratorReportTypeOption}-${target}`} key={target} value={target}>
                <ListItemText
                  primary={t(reportTypeLabel(target), reportTypeLabel(target))}
                />
              </MenuItem>
            ))}
          </Select>
          {reportType &&
          [ReportType.DriverMileagePerTrip, ReportType.TripSheet].includes(
            reportType
          ) ? (
            <Autocomplete data-testid={testIds.reportGeneratorDriverPicker}
              disablePortal
              fullWidth
              options={drivers}
              getOptionLabel={driverLabel}
              onChange={(event, driver) => {
                setFilter({
                  driver: driver?._id,
                });
              }}
              value={
                drivers.find((driver) => driver._id === filter.driver) || null
              }
              size="small"
              renderInput={(inputProps) => (
                <TextField
                  {...inputProps}
                  label="Driver"
                  inputProps={{ ...inputProps.inputProps, "data-testid": testIds.reportGeneratorDriverSearch }}
                />
              )}
            />
          ) : null}

          {reportType && [ReportType.DeliveryReport].includes(reportType) ? (
            <>
              <StateSelect data-testid="report-generator-origin-state"
                label={t("originState", "Origin State")}
                useAbbreviations={true}
                onChange={(event) => {
                  setFilter((prev) => ({
                    ...prev,
                    originState: event.target.value || null,
                  }));
                }}
                value={filter.originState}
                size="small"
                showAllOption
              />
              <StateSelect data-testid="report-generator-destination-state"
                label={t("destinationState", "Destination State")}
                useAbbreviations={true}
                onChange={(event) => {
                  setFilter((prev) => ({
                    ...prev,
                    destinationState: event.target.value || null,
                  }));
                }}
                value={filter.destinationState || null}
                size="small"
                showAllOption
              />
            </>
          ) : null}
        </Stack>
        <DatePicker
          value={periodStart}
          onChange={(value) => {
            if (!value) {
              return;
            }
            if (!isValid(value)) {
              return;
            }
            setPeriodStart(value);
          }}
          renderInput={(params) => <TextField {...params} />}
        />
        <DatePicker
          value={periodEnd}
          onChange={(value) => {
            if (!value) {
              return;
            }
            if (!isValid(value)) {
              return;
            }
            setPeriodEnd(value);
          }}
          renderInput={(params) => <TextField {...params} />}
        />
        <Stack spacing={1}>
          <LoadingButton data-testid={testIds.reportGeneratorGenerateAndDownloadPdf}
            variant="outlined"
            loading={generating}
            onClick={() => {
              if (!reportType) {
                return;
              }
              onGenerateAndDownloadReport(
                reportType,
                {
                  start: periodStart,
                  end: periodEnd,
                },
                filter
              );
            }}
            disabled={!reportType || generating}
          >
            {t("generateAndDownloadPdf", "Generate and download PDF")}
          </LoadingButton>

          <LoadingButton data-testid={testIds.reportGeneratorDownloadCsv}
            variant="outlined"
            loading={generatingCsv}
            onClick={() => {
              if (!reportType) {
                return;
              }
              onGenerateAndDownloadCsvReport(
                reportType,
                {
                  start: periodStart,
                  end: periodEnd,
                },
                filter
              );
            }}
            disabled={!reportType || generatingCsv}
          >
            {t("generateAndDownloadCsv", "Generate and download CSV")}
          </LoadingButton>

          <LoadingButton data-testid={testIds.reportGeneratorSendByEmail}
            variant="outlined"
            loading={sending}
            onClick={() => setEmailDialogOpen(true)}
          >
            {t("sendReportByEmail", "Send by email")}
          </LoadingButton>
        </Stack>
      </Stack>

      <Dialog
        open={emailDialogOpen}
        onClose={() => setEmailDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {t("sendReportByEmail", "Send report by email")}
        </DialogTitle>
        <DialogContent>
          <Stack
            direction="column"
            spacing={2}
            sx={{ p: 2 }}
            component="form"
            onSubmit={(event) => {
              event.preventDefault();
              if (!reportType) {
                return;
              }
              onSendReport(
                reportType,
                {
                  start: periodStart,
                  end: periodEnd,
                },
                filter,
                email
              );
              setEmail("");
              setEmailDialogOpen(false);
            }}
          >
            <TextField
              label="Email"
              type="email"
              value={email}
              inputProps={{ "data-testid": testIds.reportGeneratorEmail } as React.InputHTMLAttributes<HTMLInputElement>}
              onChange={(event) => setEmail(event.target.value)}
            />
            <LoadingButton data-testid={testIds.reportGeneratorSend} type="submit" loading={sending} disabled={!email}>
              {t("send", "Send")}
            </LoadingButton>
          </Stack>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ReportGenerator;
